package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.dom.svg.Circle
import com.varabyte.kobweb.compose.dom.svg.SVGFillType
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeLineCap
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.dom.svg.ViewBox
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val RING_VIEW_BOX_SIZE = 48
private const val RING_CENTER = 24
private const val RING_RADIUS = 19
private const val RING_STROKE_WIDTH = 4

/**
 * The bare-trough gap between two touching arcs, in CSS pixels.
 *
 * **Two data arcs on this ring measured 1.10:1 against each other in Day and 1.01:1 in Night** —
 * for practical purposes one arc — and they met with no separation at all, so a reader saw a single
 * band whose colour changed somewhere they could not locate. The house answer to adjacent fills
 * that carry meaning is not a louder hue: `README.md` settles it as "segments are separated by a
 * 2px bare-trough gap — a shape, not a hue", which is what `LSegmentBar` already does with
 * `ChartPalette.SURFACE_GAP`. This is the same 2px, on a circle.
 */
private const val RING_TROUGH_GAP_PX = 2.0

/** The drawn circumference, in view-box units: 2 pi r at [RING_RADIUS]. */
private val RING_CIRCUMFERENCE = 2.0 * kotlin.math.PI * RING_RADIUS

enum class LProgressRingSize(val pixels: Int) {
    SM(40),
    MD(56),
    LG(72),
}

val LProgressRingStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Block)
        .styleModifier { property("flex-shrink", "0") }
}

/**
 * The count beneath the ring — the same [progressTrailingLabel] `ProgressCell` prints beside
 * [LProgressBar], not a percentage. `PRODUCT.md` principle 4: localization progress is discrete,
 * and a percentage hides the number the reader actually needs. Plain text, not aria-hidden: unlike
 * the arcs above it, this is content, not decoration — the ring's own `aria-valuetext` says the
 * identical thing to a screen reader, so the two are one fact stated twice, not two facts.
 */
val LProgressRingLabelStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Small)
        .fontWeight(600)
        .tabularNumbers()
}

@Composable
fun LProgressRing(
    progress: LProgressCounts,
    modifier: Modifier = Modifier,
    label: String = t("ds.ds.lprogress_ring.translation_progress", "Translation progress"),
    size: LProgressRingSize = LProgressRingSize.MD,
) {
    val colorMode = ColorMode.current
    val approvedPercent = progress.bucketPercent(progress.approved)
    val translatedPercent = progress.bucketPercent(progress.translated)
    // Only where two arcs actually touch. A gap drawn against the trough is not a gap, it is a
    // shorter arc that under-reports the number it is plotting.
    val gap = if (approvedPercent > 0.0 && translatedPercent > 0.0) size.troughGap() else 0.0
    // The gap is taken out of the approved arc's trailing end, and never more than half of it: at
    // 1% approved a hairline that survives beats a full-width gap that erased the only mark saying
    // anything was approved at all.
    val approvedArc = (approvedPercent - gap).coerceAtLeast(approvedPercent / 2.0)
    // **A ring has two junctions, not one.** The second one only exists when the two arcs together
    // fill the circle — a locale fully translated and partly approved, which is the ordinary shape
    // of a locale under review — and there the translated arc's end runs into the approved arc's
    // start at twelve o'clock with nothing between them. Approved is `success` and translated is
    // `textMuted`, which measure 1.02:1 against each other in Day: with no gap the ring reads as one
    // unbroken band carrying a single notch, and the reader cannot tell which side of that notch is
    // the approved half. The same half-width floor applies, for the same reason.
    val closesTheCircle = gap > 0.0 && approvedPercent + translatedPercent >= 100.0
    val translatedArc = if (closesTheCircle) {
        (translatedPercent - gap).coerceAtLeast(translatedPercent / 2.0)
    } else {
        translatedPercent
    }

    Column(modifier.gap(Space.XXS), horizontalAlignment = Alignment.CenterHorizontally) {
        Svg(
            attrs = LProgressRingStyle.toModifier()
                .width(size.pixels.px)
                .height(size.pixels.px)
                .toAttrs {
                    viewBox(ViewBox.sized(RING_VIEW_BOX_SIZE))
                    attr("role", "progressbar")
                    attr("aria-label", label)
                    attr("aria-valuemin", "0")
                    attr("aria-valuemax", "100")
                    attr("aria-valuenow", progress.completionPercent.toString())
                    attr("aria-valuetext", progress.accessibleValueText())
                },
        ) {
            // The trough is the recessed well, not a boundary — `surfaceSunken`, the same ground
            // `LSegmentBar` sits its segments in. It was `borderStrong`, which put both arcs at 1.45
            // and 1.59 against the thing they are drawn on in Day. Against this ground the grey arc
            // measures 4.74 and the green 4.83 in Day, 6.52 and 8.50 in Night — all clear of rule
            // 10's 3:1 floor for a non-text mark, computed from the shipped hexes.
            Circle {
                cx(RING_CENTER)
                cy(RING_CENTER)
                r(RING_RADIUS)
                fill(SVGFillType.None)
                stroke(ColorTokens.SurfaceSunken.value())
                strokeWidth(RING_STROKE_WIDTH)
            }
            if (translatedPercent > 0.0) {
                Circle {
                    progressArc(
                        percent = translatedArc,
                        offset = -approvedPercent,
                        // The state rail, not this file's own opinion. This arc used to be `info` —
                        // the one blue `LProgressBar` rejects by name for the identical state.
                        color = ChartPalette.forState(colorMode, ChartState.TRANSLATED),
                    )
                }
            }
            if (approvedPercent > 0.0) {
                Circle {
                    progressArc(
                        percent = approvedArc,
                        offset = 0.0,
                        color = ChartPalette.forState(colorMode, ChartState.APPROVED),
                    )
                }
            }
        }
        Span(LProgressRingLabelStyle.toModifier().toAttrs()) {
            Text(progressTrailingLabel(progress))
        }
    }
}

private fun LProgressCounts.bucketPercent(count: Int): Double =
    if (total == 0) 0.0 else count * 100.0 / total

/**
 * [RING_TROUGH_GAP_PX] expressed in the units the dash array is written in.
 *
 * `pathLength="100"` makes an arc's dash length a percentage of the whole circle, which is what
 * lets the arcs be given as percentages — but it also means a fixed gap is *not* a fixed number
 * there: the same 1.4 units is 2px on the 56px ring and 1.2px on the 40px one. Converting through
 * the rendered scale keeps the separation the 2px the rule asks for at every size, rather than at
 * whichever size it was tuned against.
 */
private fun LProgressRingSize.troughGap(): Double {
    val scale = pixels.toDouble() / RING_VIEW_BOX_SIZE
    return (RING_TROUGH_GAP_PX / scale) / RING_CIRCUMFERENCE * 100.0
}

private fun com.varabyte.kobweb.compose.dom.svg.SVGCircleAttrsScope.progressArc(
    percent: Double,
    offset: Double,
    color: org.jetbrains.compose.web.css.CSSColorValue,
) {
    cx(RING_CENTER)
    cy(RING_CENTER)
    r(RING_RADIUS)
    fill(SVGFillType.None)
    stroke(color)
    strokeWidth(RING_STROKE_WIDTH)
    // Butt, not Round. A round cap adds half the 4px stroke past each end of the dash, so two
    // abutting arcs overlapped by 4px in total and the gap subtracted above would have been given
    // straight back — the cap would have painted over it.
    strokeLineCap(SVGStrokeLineCap.Butt)
    strokeDashArray(percent, 100.0 - percent)
    strokeDashOffset(offset)
    attr("pathLength", "100")
    transform { rotate(-90, RING_CENTER, RING_CENTER) }
}
